import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const COLOUR_STYLES = {
  green: 'bg-emerald-500/70 text-white',
  yellow: 'bg-amber-500/70 text-slate-900',
  red: 'bg-red-500/70 text-white',
  grey: 'bg-slate-700/50 text-slate-400',
};

const ABHHookHeatmap = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // selected cell

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/abh/hook-heatmap`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!data) return null;

  if (!data.visible) {
    return (
      <div
        data-testid="abh-hook-heatmap-empty"
        className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 text-center"
      >
        <p className="text-slate-400 text-sm">{data.empty_message}</p>
      </div>
    );
  }

  const byRow = {};
  (data.cells || []).forEach((c) => {
    byRow[c.hook_type] = byRow[c.hook_type] || {};
    byRow[c.hook_type][c.channel] = c;
  });

  return (
    <div
      data-testid="abh-hook-heatmap"
      className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-slate-200 text-sm font-medium">Hook Performance Heatmap</h3>
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500/70"></span>≥40%</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500/70"></span>25–40%</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500/70"></span>&lt;25%</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-700/50"></span>no data</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="py-1.5 px-2 text-left font-normal text-slate-500">Hook type</th>
              {data.channels.map((ch) => (
                <th key={ch} className="py-1.5 px-2 text-left font-normal text-slate-500">{ch}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.hook_types.map((ht) => (
              <tr key={ht}>
                <td className="py-1.5 px-2 text-slate-300">{ht}</td>
                {data.channels.map((ch) => {
                  const cell = byRow[ht]?.[ch];
                  if (!cell) return <td key={ch} />;
                  const style = COLOUR_STYLES[cell.colour] || COLOUR_STYLES.grey;
                  return (
                    <td key={ch} className="py-1 px-1">
                      <button
                        data-testid={`heatmap-cell-${ht.toLowerCase()}-${ch.toLowerCase()}`}
                        onClick={() => setActive(cell)}
                        className={`w-full rounded-md px-2 py-1.5 ${style} hover:ring-1 hover:ring-white/30 transition-all text-left`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium">
                            {cell.n > 0 ? `${(cell.avg_thumb_stop_rate * 100).toFixed(1)}%` : '—'}
                          </span>
                          <span className="text-[10px] opacity-80">n={cell.n}</span>
                        </div>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {active && active.top_hook && (
        <div
          data-testid="abh-heatmap-detail"
          className="bg-slate-800/40 rounded-lg px-3 py-2.5 border border-slate-700"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-slate-500">
                Top {active.hook_type} hook on {active.channel}
              </p>
              <p className="text-sm text-slate-200 mt-1">{active.top_hook.hook_text}</p>
              <p className="text-xs text-slate-500 mt-1">
                Thumb stop {(active.top_hook.thumb_stop_rate * 100).toFixed(1)}%
                · confidence {active.confidence_tier}
              </p>
            </div>
            <button
              onClick={() => setActive(null)}
              className="text-xs text-slate-500 hover:text-slate-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ABHHookHeatmap;
